import { useEffect, useMemo, useState } from 'react'
import axios from 'axios'
import parse from 'html-react-parser'
import moment from 'moment'
import { MapPin } from 'lucide-react'
import { Spinner } from 'components/ui/spinner'
import { Avatar, AvatarImage, AvatarFallback } from 'components/ui/avatar'
import { LocationGroupCard, LocationGroupCardLocation } from 'components/LocationGroupCard'
import { SearchInput } from 'components/SearchInput'
import { filterNotesBySearch } from 'utils/noteSearch'
import { withAuth } from 'utils/authHeaders'

type Note = {
	_id: string
	parentid?: string | null
	note: string
	createdAt?: string
	updatedAt?: string
	userid?: {
		name?: string
		email?: string
		image?: string
	}
}

type LocationLite = LocationGroupCardLocation & {
	_id: string
}

const formatNoteDate = (dateStr?: string) => {
	if (!dateStr) return ''
	const m = moment(dateStr)
	if (m.isSame(moment(), 'day')) return `Today, ${m.format('h:mm a')}`
	if (m.isAfter(moment().subtract(7, 'days'))) return m.format('dddd, h:mm a')
	return m.format('MMM D, YYYY')
}

const mostRecentTime = (notes: Note[]): number => {
	if (notes.length === 0) return 0
	let max = 0
	for (const n of notes) {
		const t = new Date(n.updatedAt || n.createdAt || 0).getTime()
		if (t > max) max = t
	}
	return max
}

interface PortfolioLocationNotesProps {
	portfolio: any
	className?: string
}

/**
 * Read-only roll-up of the notes written against each location in a portfolio,
 * grouped by location and sorted by most recent activity.
 *
 * Extracted from PortfolioNotesCN when the old Notes feed was retired, so the
 * roll-up outlives it. It answers a different question from the portfolio's own
 * feed ("what is happening across these assets?" rather than "what has been
 * said about the portfolio?"), which is why it is a section of its own rather
 * than notes merged into the feed.
 *
 * Fetches per location with plain axios rather than through a slice: these
 * notes belong to other records, are never edited here, and writing them into
 * the location-notes slice would fight with whichever location the user visits
 * next.
 */
const PortfolioLocationNotes = ({ portfolio, className }: PortfolioLocationNotesProps) => {
	const locationsList: LocationLite[] = useMemo(() => {
		const raw = portfolio?.locations || []
		return raw
			.map((entry: any) => entry?.location)
			.filter(Boolean)
			.map((loc: any) => ({
				_id: loc._id,
				image: loc.image,
				assetName: loc.assetName || '',
			}))
	}, [portfolio?.locations])

	const [locationSearch, setLocationSearch] = useState('')
	const [notesByLocationId, setNotesByLocationId] = useState<Record<string, Note[]>>({})
	const [locationNotesLoading, setLocationNotesLoading] = useState(false)

	useEffect(() => {
		if (locationsList.length === 0) {
			setNotesByLocationId({})
			return
		}
		let cancelled = false
		setLocationNotesLoading(true)
		Promise.all(
			locationsList.map(async loc => {
				try {
					const { data } = await axios.get(
						'/api/locationnotes/notes',
						withAuth({ params: { locationid: loc._id } }),
					)
					return [loc._id, data as Note[]] as const
				} catch {
					return [loc._id, [] as Note[]] as const
				}
			}),
		).then(results => {
			if (cancelled) return
			setNotesByLocationId(Object.fromEntries(results))
			setLocationNotesLoading(false)
		})
		return () => {
			cancelled = true
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [portfolio?._id, locationsList.length])

	const groupsSorted = useMemo(() => {
		return locationsList
			.map(location => ({
				location,
				notes: (notesByLocationId[location._id] ?? []).filter(
					n => n.parentid === null || n.parentid === undefined,
				),
			}))
			.filter(g => g.notes.length > 0)
			.sort((a, b) => mostRecentTime(b.notes) - mostRecentTime(a.notes))
	}, [locationsList, notesByLocationId])

	// Filter inside each group, then drop groups the query emptied. This
	// roll-up is read-only and has no image rows to hand in, so it matches on
	// note text alone.
	const visibleGroups = useMemo(() => {
		if (!locationSearch.trim()) return groupsSorted
		return groupsSorted
			.map(group => ({
				...group,
				notes: filterNotesBySearch(group.notes, locationSearch),
			}))
			.filter(group => group.notes.length > 0)
	}, [groupsSorted, locationSearch])

	const totalLocationNotes = groupsSorted.reduce((sum, g) => sum + g.notes.length, 0)

	if (!portfolio) return null

	return (
		<section className={className} data-testid="portfolio-location-notes">
			<div className="flex flex-wrap items-center gap-2 border-t border-gray-200 pt-4">
				<MapPin className="h-4 w-4 text-gray-500" />
				<h3 className="m-0 text-base font-semibold text-gray-700">Location Notes</h3>
				{locationNotesLoading && <Spinner containerHeight="auto" />}
				{totalLocationNotes > 0 && (
					<SearchInput
						value={locationSearch}
						onChange={setLocationSearch}
						placeholder="Search location notes..."
						className="ml-auto w-full sm:w-64"
					/>
				)}
			</div>

			{!locationNotesLoading && groupsSorted.length === 0 && (
				<p className="mt-3 text-sm italic text-gray-500">
					No notes have been added to any location in this portfolio.
				</p>
			)}

			{!locationNotesLoading && groupsSorted.length > 0 && visibleGroups.length === 0 && (
				<p className="mt-3 text-sm italic text-gray-500">
					No location notes match “{locationSearch.trim()}”.
				</p>
			)}

			<div className="mt-3 space-y-4">
				{visibleGroups.map(({ location, notes }) => (
					<LocationGroupCard key={location._id} location={location} itemCount={notes.length}>
						<ul className="m-0 list-none space-y-3 p-0">
							{notes.map(note => (
								<li
									key={note._id}
									className="border-b border-gray-100 pb-3 last:border-b-0 last:pb-0"
								>
									<div className="mb-1.5 flex items-center gap-2">
										<Avatar className="h-6 w-6">
											<AvatarImage src={note.userid?.image} />
											<AvatarFallback className="text-[10px]">
												{note.userid?.name?.charAt(0)?.toUpperCase() || 'U'}
											</AvatarFallback>
										</Avatar>
										<span className="text-xs font-medium text-gray-900">
											{note.userid?.name || 'Unknown'}
										</span>
										<span className="text-xs text-gray-300">·</span>
										<span className="text-xs text-gray-500">
											{formatNoteDate(note.createdAt)}
										</span>
									</div>
									<div className="text-sm leading-relaxed text-gray-700 [&_img]:my-2 [&_img]:max-w-full [&_img]:rounded-md [&_p]:m-0 [&_p+p]:mt-2">
										{parse(note.note || '')}
									</div>
								</li>
							))}
						</ul>
					</LocationGroupCard>
				))}
			</div>
		</section>
	)
}

export default PortfolioLocationNotes
